// DeviceBasedRenderer.jsx
// 기기별 컴포넌트 자동 선택 HOC (Higher-Order Component)

import React from "react";
import { useAtom } from "jotai";
import { DEVICE_TYPE } from "../../pages/AtomStates";
import { DEVICE_TYPES } from "../../utils/DeviceDetectionService";

/**
 * 기기 타입에 따라 적절한 컴포넌트를 렌더링하는 HOC
 *
 * @param {Object} props
 * @param {React.Component} props.DesktopComponent - 데스크탑용 컴포넌트
 * @param {React.Component} props.MobileComponent - 모바일용 컴포넌트
 * @param {React.Component} props.AndroidComponent - 안드로이드 전용 컴포넌트 (선택사항)
 * @param {React.Component} props.IOSComponent - iOS 전용 컴포넌트 (선택사항)
 * @param {React.Component} props.FallbackComponent - 대체 컴포넌트 (선택사항)
 * @param {Object} props.componentProps - 하위 컴포넌트에 전달할 props
 * @returns {React.Component}
 */
const DeviceBasedRenderer = ({
  DesktopComponent,
  MobileComponent,
  AndroidComponent,
  IOSComponent,
  FallbackComponent,
  ...componentProps
}) => {
  const [deviceType] = useAtom(DEVICE_TYPE);

  console.log("🎯 DeviceBasedRenderer - 현재 기기 타입:", deviceType);

  // 기기별 컴포넌트 선택 로직
  switch (deviceType) {
    case DEVICE_TYPES.ANDROID:
      if (AndroidComponent) {
        console.log("📱 안드로이드 전용 컴포넌트 렌더링");
        return <AndroidComponent {...componentProps} />;
      }
      // 안드로이드 전용 컴포넌트가 없으면 모바일 컴포넌트 사용
      if (MobileComponent) {
        console.log("📱 안드로이드 → 모바일 컴포넌트 렌더링");
        return <MobileComponent {...componentProps} />;
      }
      break;

    case DEVICE_TYPES.IOS:
      if (IOSComponent) {
        console.log("🍎 iOS 전용 컴포넌트 렌더링");
        return <IOSComponent {...componentProps} />;
      }
      // iOS 전용 컴포넌트가 없으면 모바일 컴포넌트 사용
      if (MobileComponent) {
        console.log("🍎 iOS → 모바일 컴포넌트 렌더링");
        return <MobileComponent {...componentProps} />;
      }
      break;

    case DEVICE_TYPES.MOBILE:
      if (MobileComponent) {
        console.log("📱 모바일 컴포넌트 렌더링");
        return <MobileComponent {...componentProps} />;
      }
      break;

    case DEVICE_TYPES.DESKTOP:
    default:
      if (DesktopComponent) {
        console.log("🖥️ 데스크탑 컴포넌트 렌더링");
        return <DesktopComponent {...componentProps} />;
      }
      break;
  }

  // 대체 컴포넌트가 있으면 사용
  if (FallbackComponent) {
    console.log("⚠️ 대체 컴포넌트 렌더링");
    return <FallbackComponent {...componentProps} />;
  }

  // 데스크탑 컴포넌트를 기본값으로 사용
  if (DesktopComponent) {
    console.log("🖥️ 기본값으로 데스크탑 컴포넌트 렌더링");
    return <DesktopComponent {...componentProps} />;
  }

  // 모든 컴포넌트가 없으면 에러 표시
  console.error("❌ DeviceBasedRenderer: 렌더링할 컴포넌트가 없습니다", {
    deviceType,
    hasDesktop: !!DesktopComponent,
    hasMobile: !!MobileComponent,
    hasAndroid: !!AndroidComponent,
    hasIOS: !!IOSComponent,
    hasFallback: !!FallbackComponent,
  });

  return (
    <div
      style={{
        padding: "20px",
        textAlign: "center",
        color: "red",
        border: "2px solid red",
        borderRadius: "8px",
        margin: "20px",
      }}
    >
      <h3>⚠️ 컴포넌트 렌더링 오류</h3>
      <p>기기 타입: {deviceType}</p>
      <p>해당 기기에 맞는 컴포넌트를 찾을 수 없습니다.</p>
    </div>
  );
};

export default DeviceBasedRenderer;
